// @flow
import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import Vue from 'vue';

import { translate, translateText } from '../scripts/core/tools/translate';
import api from './api';
import bootstrap from './bootstrap';
import environment from './environment';
import AuthService from '../services/authservice';
import { FeaturePermission } from '../constants/feature.permission.helper';
import { CompanySizeEnum } from '../enuns/companySize.enum';
import { VehicleTypeEnum } from './core/vehicleType/constants';
import { CountryEnum, getCountryAcronym } from '../enuns/country.enum';
import { CONFIG } from '../config';
import fetch from './api/fetch';
import { destroyIntercom, openIntercom, startSurveyIntercom } from '../helpers/intercom.helper';
import { NetworkStoreSizeTypes } from '../constants/NetworkStoreSizeTypes';
import { getCountry } from '../utils';
import { i18nMixin } from '../modules/shared/mixins';
import Storage from './storage';
import { SaleTypeEnum } from '../enuns/saleType.enum';

// declare var bootstrap;
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class App {
    async addIntercomMessage(userInfo) {
        const authUser = await AuthService.getUser();
        window.intercomSettings = {
            api_base: "https://api-iam.intercom.io",
            app_id: "p51ayt5q",
            name: authUser.profile.name,
            email: authUser.profile.email,
            created_at: authUser.profile.auth_time,
            phone: authUser.profile.phone_number,
            user_hash: authUser.profile.intercom_hmac,
            alignment: 'right',
            horizontal_padding: 20,
            vertical_padding: 55,
            session_duration: 5 * 60 * 1000,
            "departament": authUser.profile.Office,
            "contract_brand": userInfo.clientName,
            "contract_country": userInfo.countryName
        };

        (function () {
            const w = window;
            const ic = w.Intercom;
            if (typeof ic === 'function') {
                ic('reattach_activator');
            } else {
                const d = document;
                const i = function () {
                    i.c(arguments);
                };
                i.q = [];
                i.c = function (args) {
                    i.q.push(args);
                };
                w.Intercom = i;
                const l = function () {
                    const s = d.createElement('script');
                    s.type = 'text/javascript';
                    s.async = true;
                    s.src = 'https://widget.intercom.io/widget/p51ayt5q';
                    const x = d.getElementsByTagName('script')[0];
                    x.parentNode.insertBefore(s, x);
                };
                if (document.readyState === 'complete') {
                    l();
                } else if (w.attachEvent) {
                    w.attachEvent('onload', l);
                } else {
                    w.addEventListener('load', l, false);
                }
            }

            destroyIntercom();
        })();
    }

    async run() {
        if (await AuthService.isAuthenticated()) {
            const data = await fetch({ url: `${CONFIG.URL.API_HOST}/general/user/info`, type: 'GET' });
            await this.addIntercomMessage(data);
            window.NeoGIG.country = getCountryAcronym(data.country);
            api.general.get.config()
                .then((res) => {
                    this._setConfig(res)
                        .then(() => {
                            this._setAuthorizations(res);
                            this._setPermissions();
                            this._setLayerTypes(res);
                            bootstrap.doLogin(true);
                        });
                });

            startSurveyIntercom();
        } else {
            AuthService.login();
        }
    }

    _setAuthorizations(res) {
        api.authorization(res.authorization);
    }

    _setPermissions() {
        const afterSalesFeatures = {
            NetworkAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_NETWORK.enable),
            CompetitorsAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_COMPETITORS.enable),
            StoreInactiveAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_STORE_INACTIVE.enable),
            DealershipAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_DEALERSHIP.enable),
            CustomersAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CUSTOMER.enable),
            ClientSalesAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_CLIENT_SALES.enable),
            StoreAreasAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_AREA_DEALER.enable),
            MarkedAreaAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_AREA_CLIENT.enable),
            MulticriteriaAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_MULTICRITERIA.enable),
            SelectedAreasAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_TERRITORIAL_BOUNDARIES.enable),
            CompanyCustomersPotentialAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_COMPANY_CUSTOMERS_POTENTIAL.enable),
            ServiceAreaAfterSales: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_SERVICE_AREA.enable),
        };

        environment.set('FEATURES', {
            Maps: true,
            ...afterSalesFeatures,
            Dealerships: isAuthorized(FeaturePermission.LAYER_NETWORK.enable),
            StoresMotocycle: isAuthorized(FeaturePermission.LAYER_STORES_MOTOCYCLE.enable),
            Competitors: isAuthorized(FeaturePermission.LAYER_COMPETITORS.enable),
            StoresConsortium: isAuthorized(FeaturePermission.LAYER_STORES_CONSORTIUM.enable),
            StoreInactive: isAuthorized(FeaturePermission.LAYER_STORE_INACTIVE.enable),
            UnapprovedStores: isAuthorized(FeaturePermission.LAYER_UNAPPROVED_STORES.enable),
            StorePlanned: isAuthorized(FeaturePermission.LAYER_PLANNED_STORES.enable),
            MallCenters: isAuthorized(FeaturePermission.LAYER_MALL_CENTERS.enable),
            ClusterSales: isAuthorized(FeaturePermission.LAYER_CLUSTER_SALES.enable),
            PointOfInterest: isAuthorized(FeaturePermission.LAYER_POINT_INTEREST.enable),
            Brownfield: isAuthorized(FeaturePermission.LAYER_BROWNFIELD.enable),
            Greenfield: isAuthorized(FeaturePermission.LAYER_GREENFIELD.enable),
            NewGreenfield: isAuthorized(FeaturePermission.LAYER_NEW_GREENFIELD.enable),
            DirectSales: isAuthorized(FeaturePermission.LAYER_DIRECT_SALE.enable),
            Customers: isAuthorized(FeaturePermission.LAYER_CUSTOMER.enable),
            ServiceArea: isAuthorized(FeaturePermission.LAYER_AREA_DEALER.selectArea),
            Voronoi: isAuthorized(FeaturePermission.LAYER_VORONOI.enable),
            Report: isAuthorized(FeaturePermission.SYSTEM.showNeodash),
            Toll: isAuthorized(FeaturePermission.LAYER_GENERAL.tollEnable),
            DncFlexible: isAuthorized(FeaturePermission.LAYER_DNC.enable),
            MarkedArea: isAuthorized(FeaturePermission.LAYER_AREA_CLIENT.enable),
            SelectedAreas: isAuthorized(FeaturePermission.LAYER_LOCALES.enable),
            AreaClient: isAuthorized(FeaturePermission.LAYER_LOCALES.enableDga),
            Cities: isAuthorized('host-cities-view'),
            FuelStation: isAuthorized(FeaturePermission.LAYER_GENERAL.gasStationEnable),
            WorkshopPart: isAuthorized(FeaturePermission.LAYER_WORKSHOP_PART.enable),
            PGTS: isAuthorized(FeaturePermission.LAYER_GENERAL.pgtsEnable),
            BroadcastTV: isAuthorized('standard-group-tv-repeaters'),
            FarmMachine: isAuthorized(FeaturePermission.LAYER_GENERAL.farmMachineEnable),
        });
    }

    _setLayerTypes(res) {
        const TYPE = {
            AUTO: -1,
            DYNAMIC: -1
        };

        _.forEach([res.areas, res.groupings], (types) => {
            _.forEach(types, (val, key) => {
                TYPE[key.toUpperCase()] = val.id;
            });
        });

        environment.set('TYPE', TYPE);
    }

    _isNeogigAfterSales(res) {
        return res.marketplace === 5;
    }

    _setConfig(res) {
        const country = getCountry();
        if (country !== undefined && country !== CountryEnum.Brasil.toLowerCase()) {
            const inforboardState = Storage.get('country');
            if (inforboardState === null) {
                Storage.save('country', country);
                i18nMixin.methods.setLanguage('es-AS');
            }
        };

        if (this._isNeogigAfterSales(res)) {

            return new Promise((resolve) => {
                const CONFIG = {};
                CONFIG.IDUSER = res.idUser;
                CONFIG.MARKETPLACE = res.marketplace;
                CONFIG.IS_AFTER_SALES = true;
                CONFIG.CLIENT = res.client;
                CONFIG.VEHICLETYPES = res.vehicleTypes;
                CONFIG.USERVEHICLETYPE = res.vehicleType;
                CONFIG.WORKSPACE = res.workspace;
                CONFIG.AUTHORIZEDBRANDS = res.authorizedBrands;
                CONFIG.STORES_VEHICLE_TYPE = res.dealershipsBrands;
                CONFIG.STATES = res.states;
                CONFIG.CLIENT_TOTAL_SALES = res.clientTotalSales;

                CONFIG.FILTERS = {
                    FILTERBY: res.searchFilter,
                    PGTS: res.tgcTypes.map(t => ({ ...t, nome: translateText(t.nome, false, false) })),
                    MAPTHEME_PGTS: res.mapThemeTgcTypes.map(t => ({ ...t, nome: translateText(t.nome, false, false) })),
                    AREAS_TYPES: res.areas,
                    AREAS_THEMES: res.areasDynamicThemes,
                    MARKED_AREA_TYPES: res.groupings,
                    MARKED_AREA_THEMES: res.groupingDynamicThemes,
                    SEGMENTS_CLIENT: res.afterSalesSegments,
                    SALES_TYPES: res.afterSalesSalesTypes,
                    SERVICE_TYPES: res.serviceTypes,
                    NETWORK_TYPES: res.networkTypes,
                    NETWORKS: res.networks,
                    CLIENT_NETWORKS: res.clientNetworks,
                    BRANDS: res.afterSalesBrands,
                    CLIENT_BRANDS: res.clientAfterSalesBrands,
                    SALES_PERIOD: res.salesPeriod,
                    CLASSIFICATION_NETWORK_STORE: [
                        { id: NetworkStoreSizeTypes.CompetitorsMain, name: 'Principais Concorrentes' },
                        { id: NetworkStoreSizeTypes.CompetitorsGreaterThanTwo, name: 'Outros (mais de 2 lojas)' },
                        { id: NetworkStoreSizeTypes.CompetitorsLessThanTwo, name: 'Outros (1 a 2 lojas)' },
                    ],
                    DEALER_TYPES: res.dealerTypes,
                    DEALER_STATE_ACRONYMS: res.dealerStateAcronyms,
                    COMPETITORS_STATE_ACRONYMS: res.competitorsStateAcronyms,
                    POINTS_TYPES: res.pointTypes,
                    STORE_TYPES: res.storeTypes,
                    DEALERSHIPS_BRANDS: res.dealershipsBrands,
                    CUSTOMERS_PERIOD: res.customersPeriod,
                    CLIENT_COMPANY_PERIOD: res.clientCompanyPeriod,
                    CLIENT_COMPANY_TYPE: res.clientCompanyType,
                    CLIENT_COMPANY_TIER: res.clientCompanyTier,
                    CLIENT_TYPE_SEGMENT_MARKET: res.typeSegmentMarket,
                    CUSTOMERS_STATE_ACRONYMS: res.customersStateAcronyms,
                    SALES_CUSTOMER_TYPES: res.salesCustomerTypes,
                    FUEL_TYPES: res.fuelTypes,
                    GROUPS_NAME: res.economicGroups,
                    SCORE_RANGES: res.scoreRanges,
                    TRAFFIC_GENERATOR_POLE_COMPANY_SIZES: res.trafficGeneratorPoleCompanySizes,
                    ENGINE_TYPE: res.engineTypes,
                    SOCIAL_CAPITAL_TYPES: res.socialCapitalTypes,
                };

                Promise.all([
                    api.fuelStation.listAllBrands()
                ]).then(([fuelStationBrands]) => {
                    CONFIG.FILTERS.FUEL_STATION_BRANDS = fuelStationBrands;
                });

                environment.set('CONFIG', CONFIG);
                resolve();
            });
        }

        return new Promise((resolve) => {
            const CONFIG = {};
            CONFIG.IS_AFTER_SALES = false;
            CONFIG.IDUSER = res.idUser;
            CONFIG.AUTHORIZEDBRANDS = res.authorizedBrands;
            CONFIG.HASNEEDACCEPTTERMSUSE = res.hasNeedAcceptTermsUse;
            CONFIG.HASACCEPTTERMSUSE = res.hasAcceptTermsUse;
            CONFIG.STORES_VEHICLE_TYPE = [...res.dealerships, ...res.competitors];
            CONFIG.DEALERS = Array.from(res.dealerships.reduce((names, dealership) => names.add(dealership.name), new Set()));
            CONFIG.COMPETITORS = Array.from(res.competitors.reduce((names, competitor) => names.add(competitor.name), new Set()));
            CONFIG.CLIENT = res.client || (CONFIG.DEALERS[0].length > 3 ? CONFIG.DEALERS.toCapitalCase() : CONFIG.DEALERS[0]);
            CONFIG.STATES = res.states;
            CONFIG.SEGMENT = res.marketSegment;
            CONFIG.VEHICLETYPES = res.vehicleTypes;
            CONFIG.USERVEHICLETYPE = res.vehicleType;
            CONFIG.WORKSPACE = res.workspace;
            CONFIG.MAX_SIZE_UPLOAD = res.emailAttachmentMaxSize;
            CONFIG.EMAIL_ATTACHMENT_FORMATS = res.emailAttachmentAllowedExtensions;
            CONFIG.NOTIFICATIONS = {
                VHOST: res.notifications.virtualHost,
                USER: res.notifications.user,
                PASS: res.notifications.password,
                SOCKET_HOST: res.notifications.socketHost,
                QUEUE: res.notifications.queue
            };
            CONFIG.TARGET = res.target;
            CONFIG.IS_PREMIUM = res.isPremiumClient;
            CONFIG.CLIENT_TOTAL_SALES = res.clientTotalSales;
            CONFIG.FILTERS = {
                CUSTOMERS_STORES: res.idStoresDealer,
                AREAS_TYPES: res.areas,
                CLIENT_TYPES: res.clientStoreTypes.map(x => translate(x)),
                COMPETITORS_TYPES: res.competitorStoreTypes.map(x => translate(x)),
                STORE_CONTEXT_ATTRIBUTES: res.storeContextAttributes,
                STORE_COMPETITORS_CONTEXT_ATTRIBUTES: res.storeCompetitorsContextAttributes,
                DNC_FLEXIBLE_TYPES: res.dncFlexibleAreas || [],
                DIRECT_TYPES: res.ucdDirectSale,
                MARKED_AREA_TYPES: res.groupings,
                ORIGIN_DESTINY_TYPES: [],
                POINTS_TYPES: res.pointTypes,
                VORONOI_TYPES: [{
                    themes: [{ id: 11, nome: 'Grupos', mascara: '' },
                    { id: 31, nome: 'Grupos Clientes', mascara: '' },
                    ],
                    id: -1,
                    name: translate('Common.Dinamic')
                }],
                AREAS_THEMES: res.areasDynamicThemes,
                MARKED_AREA_THEMES: res.groupingDynamicThemes,
                VORONOI_THEMES: res.voronoiThemes,
                DNC_DATES: [
                    { value: 1, text: translate('Common.CurrentYear') },
                    { value: 2, text: translate('Common.LastMonth') },
                    { value: 3, text: translate('Common.12Months') }
                ],
                DNC_FLEXIBLE_DATES: [
                    { value: 1, text: translate('Common.LastMonth') },
                    { value: 2, text: translate('Common.LastYear') },
                    { value: 3, text: translate('Common.CurrentYear') },
                    { value: 4, text: translate('Common.Last12Months') }
                ],
                BRANDS_CLASSIFICATION: res.allBrandClassification,
                BRANDS_CLASSIFICATION_ACTIVE_NETWORK: res.brandWithNetworkActiveClassification,
                DATE_PREFERENCES: {}, // res.contextDateFilters,
                HAS_GROUP_CLIENT: res.hasGroupClient,
                GROUPS_CLIENTS_NAME: res.clientGroups || [],
                GROUPS_NAME: res.economicGroups,
                GROUPS_NAME_UNAPPROVED_STORE: res.economicGroupsFromUnnaprovedStores || {},
                GROUPS_WITH_PLANNED_STORES: res.groupsWithPlannedStores || {},
                GROUPS_WITH_CONSORTIUM_STORES: res.groupsWithConsortiumStores || [],
                TYPE_OF_UNAPPROVED_STORES: res.typeUnapprovedOfStores || {},
                FILTERBY: res.searchFilter,
                PGTS: res.tgcTypes.map(t => ({ ...t, nome: translateText(t.nome) })),
                SALES_CHANNEL: res.salesChannel.map(s => ({ ...s, text: translateText(s.text, true) })),
                SALES_TYPE: res.saleTypes,
                SEGMENT: res.clientSegments,
                TV_STATIONS: res.filtroEmissoras,
                FUEL_TYPES: res.fuelTypes.map(fuel => ({ ...fuel, text: translateText(fuel.text, true) })),
                COMPANY_SIZES: res.companyTypeSize.map(size => ({ ...size, text: translateText(size.name, true) })),
                TYPE_SALES_MARKET: [
                    { text: translate('Common.Auto'), value: `${VehicleTypeEnum.Car}` },
                    { text: translate('Common.Truck'), value: `${VehicleTypeEnum.Truck}` },
                    { text: translate('Common.Auto&Truck'), value: `${VehicleTypeEnum.Car}|${VehicleTypeEnum.Truck}` },
                    { text: translate('Common.Auto&Moto'), value: `${VehicleTypeEnum.Car}|${VehicleTypeEnum.Moto}` },
                ],
                SPECIALIZATION_INDUSTRY_CONTEXT: res.specializationIndustryContext,
                GREENFIELD_TYPES: res.greenFieldTypes,
                GREENFIELD_SCENARIO_YEARS: res.greenfieldScenarioYears,
                GREENFIELD_LAST_UPDATE: res.greenfieldLastUpdate,
                GREENFIELD_POINT_TIERS: res.greenfieldPointTiers,
                GREENFIELD_POINT_TYPES: res.greenfieldPointTypes,
                BROWNFIELD_SCENARIO_YEARS: res.brownfieldScenarioYears,
                BROWNFIELD_LAST_UPDATE: res.brownfieldLastUpdate,
                BROWNFIELD_ACTIONS: res.brownfieldActions,
                VEHICLEFLEETAREA_SEGMENTS_CLIENT: res.vehicleFleetAreaSegmentsCorp,
                FUEL_TYPES_VEHICLE_FLEET: res.fuelTypesVehicleFleet,
                WORKSHOP_PARTS_TYPES: res.workshopPartTypes,
                TYPE_SOCIAL_CLASS: res.classSocialTypes,
                AREAS_OPERATIONAL: res.areaOperational,
                COMPANY_SIZES_DNC_CONSOLIDATE: res.tierTypeConsolidate,
                IS_DNC_CONSOLIDATE: res.isDncConsolidate,
                TYPE_CLASSIFICATION_SALE: res.typeClassificationSale,
                MALL_CENTERS_HAVE_STORES: res.mallCenters,
                COMPETING_SEGMENTS: res.competingSegments,
            };
            CONFIG.PARAM = {};

            if (CONFIG.USERVEHICLETYPE === VehicleTypeEnum.Moto) {
                CONFIG.FILTERS.TYPE_SALES_MARKET.push({
                    text: translate('Common.Moto'),
                    value: `${VehicleTypeEnum.Moto}`
                });
            }

            Promise.all([
                api.summary.get.period(),
                api.store.get.retention.period(),
                api.store.get.all(CONFIG.DEALERS, CONFIG.FILTERS.CLIENT_TYPES),
                api.general.get.brands({
                    asArray: false
                }),
                api.store.get.directSales.period(),
                api.fuelStation.listAllBrands()
            ]).then(([summary, retention, stores, brands, directSales, fuelStationBrands]) => {
                CONFIG.FILTERS.STARTYEAR = summary.start;
                CONFIG.FILTERS.ENDYEAR = summary.end;

                if (!CONFIG.FILTERS.DATE_PREFERENCES.SUMMARY) {
                    CONFIG.FILTERS.DATE_PREFERENCES.SUMMARY = {
                        START: moment(summary.end).startOf('year').toDate(),
                        END: summary.end
                    };
                }

                const lastYear = summary.end.getFullYear();
                const qtyYearsAverageSales = 4;

                CONFIG.FILTERS.AVERAGE_SALES = Array.from({ length: qtyYearsAverageSales }, (_, i) => lastYear - i);
                CONFIG.FILTERS.RETENTION_STARTDATE = retention.start;
                CONFIG.FILTERS.RETENTION_ENDDATE = retention.end;

                CONFIG.FILTERS.DIRECTSALES_STARTDATE = directSales.start;
                CONFIG.FILTERS.DIRECTSALES_ENDDATE = directSales.end;

                CONFIG.FILTERS.DEALERS_STORES = stores;

                CONFIG.FILTERS.BRANDS = _.orderBy(_.reduce(CONFIG.FILTERS.BRANDS_CLASSIFICATION, (acc, val, key) => {
                    _.forEach(val, (v, i) => {
                        if (_.find(brands, b => v === b.name))
                            acc.push({
                                classificationId: key,
                                classification: translate(key),
                                brandId: _.find(brands, b => v === b.name).idBrand,
                                brand: v
                            });
                    });
                    return acc;
                }, []), v => v.brand);

                CONFIG.FILTERS.FUEL_STATION_BRANDS = fuelStationBrands;

                environment.set('CONFIG', CONFIG);

                openIntercom(window.intercomSettings.horizontal_padding, window.intercomSettings.vertical_padding);

                resolve();
            })
                .catch(error => {
                    console.log(error);
                });
        });
    }
}



// WEBPACK FOOTER //
// ./src/scripts/app.js